import i18next from 'i18next';
import { useCallback } from 'react';

import {
  getPermissionState,
  isAdminOnlyPermission,
  isPermissionUnconditionallyAllowed,
} from '../utils';
import { getEntityPermissionRequiredRights } from '../utils/entity-permissions-configs';

import { UsePermissionsFunctionProps } from './types';

const UNCONDITIONALLY_ALLOWED_RETURN = { allowed: true };
const ADMIN_ONLY_RETURN = {
  allowed: false,
  reason: i18next.t('USE_PERMISSIONS.ADMIN_ONLY'),
};
const CONTACT_YOUR_ADMINISTRATOR_RETURN = {
  allowed: false,
  reason: i18next.t('USE_PERMISSIONS.CONTACT_YOUR_ADMINISTRATOR'),
};

export const usePermissionsFunction = <T>({
  user,
  rights,
  permission,
}: UsePermissionsFunctionProps) => {
  const callback = useCallback(
    ({ entity, context, record, withExtraInfos = false }) => {
      const isUnconditionallyAllowed = isPermissionUnconditionallyAllowed({
        entity,
        permission,
      });

      if (isUnconditionallyAllowed) {
        return withExtraInfos ? UNCONDITIONALLY_ALLOWED_RETURN : true;
      }

      const isAdminOnly = isAdminOnlyPermission({ entity, permission });

      if (isAdminOnly) {
        const returnValue = user.isMaster
          ? UNCONDITIONALLY_ALLOWED_RETURN
          : ADMIN_ONLY_RETURN;

        return withExtraInfos ? returnValue : returnValue.allowed;
      }

      const requiredRights = getEntityPermissionRequiredRights({
        entity,
        context,
        permission,
      });

      if (requiredRights.length === 0) {
        return withExtraInfos ? CONTACT_YOUR_ADMINISTRATOR_RETURN : false;
      }

      return getPermissionState({
        user,
        record,
        rights,
        requiredRights,
        withExtraInfos,
      });
    },
    [permission, rights, user]
  );

  return callback as T;
};
